import React from "react";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";
import Tooltip from "../ui/Tooltip";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

export const ReparentMenuIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="5" cy="12" r="2.2" />
    <circle cx="19" cy="12" r="2.2" />
    <path d="M7.8 12h8.4" />
    <path d="m13.6 8.8 3.2 3.2-3.2 3.2" />
  </svg>
);

const buildGroupedBodyOptions = (options, bodies, worldBodies) => {
  const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
  const groupMap = new Map();

  (worldBodies || []).forEach((worldBody) => {
    const rootUuid =
      typeof worldBody === "string" ? worldBody : worldBody.rootBodyUuid || worldBody.uuid;
    if (!rootUuid || groupMap.has(rootUuid)) return;
    const label =
      typeof worldBody === "object" && worldBody.name
        ? worldBody.name
        : bodies?.[rootUuid]?.name || rootUuid;
    groupMap.set(rootUuid, { label, bodies: [] });
  });

  options.forEach((body) => {
    const rootUuid = rootMap[body.uuid] || body.uuid;
    if (!groupMap.has(rootUuid)) {
      groupMap.set(rootUuid, { label: bodies?.[rootUuid]?.name || rootUuid, bodies: [] });
    }
    groupMap.get(rootUuid).bodies.push(body);
  });

  const groups = Array.from(groupMap.values())
    .map((group) => ({
      ...group,
      bodies: group.bodies.sort((a, b) =>
        String(stripPrefix(a.name) || "").localeCompare(String(stripPrefix(b.name) || ""))
      ),
    }))
    .filter((group) => group.bodies.length > 0);

  return groups.length > 1 ? groups : null;
};

const QuickNavReparentMenu = ({
  options = [],
  bodies = {},
  worldBodies = [],
  onSelect,
  leadingContent = null,
  className = "mt-2 rounded-md border border-gray-600 p-0.5 shadow-xl",
  style = { backgroundColor: "rgba(40, 40, 40, 0.95)" },
}) => {
  const groupedOptions = buildGroupedBodyOptions(options, bodies, worldBodies);
  const renderOptionButton = (body) => {
    const disabledReason = body.disabled ? body.description || "This target is not valid." : "";
    const button = (
      <button
        key={body.uuid}
        type="button"
        aria-disabled={body.disabled ? "true" : undefined}
        onClick={() => {
          if (body.disabled) return;
          onSelect(body.uuid);
        }}
        className={
          body.disabled
            ? "w-full text-left rounded-sm px-2 py-1.5 text-sm panel-text-helper opacity-55 cursor-not-allowed"
            : "w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-[var(--panel-surface-hover)] transition-colors"
        }
      >
        {stripPrefix(body.name) || body.uuid}
      </button>
    );

    return disabledReason ? (
      <Tooltip key={body.uuid} content={disabledReason}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <div className={className} style={style}>
      <div className="max-h-52 overflow-y-auto space-y-0.5">
        {leadingContent}
        {groupedOptions
          ? groupedOptions.map((group) => (
              <div key={group.label}>
                <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  {stripPrefix(group.label) || group.label}
                </div>
                {group.bodies.map(renderOptionButton)}
              </div>
            ))
          : [...options]
              .sort((a, b) =>
                String(stripPrefix(a.name) || "").localeCompare(String(stripPrefix(b.name) || ""))
              )
              .map(renderOptionButton)}
      </div>
    </div>
  );
};

export default React.memo(QuickNavReparentMenu);
